// A city's page, alive: the one controller for the guide and the briefing.
//
// The markup is strings from lib/place-city.ts — already in the document when
// the page was pre-rendered, or painted here when it was not. What this adds is
// everything a string cannot carry: one selected ten-day section and one rating
// shared by the calendar, the chart, the reading column, the verdict, the month
// table and the ruler, and the handlers that change them.
//
// It runs in two places that share nothing else — the app (city/panel.ts, where
// the section and the rating are the map's own state) and a guide document
// (place/main.ts, where they are the page's). So it owns no state of that kind:
// it asks the host what is selected and tells the host what the reader picked.
// The only state here is the page's own furniture — which chart, which menu.

import { VAR } from '../../lib/variables.ts';
import type { VarKey } from '../../lib/variables.ts';
import type { Recipe } from '../../lib/recipe.ts';
import { sectionLabel } from '../../lib/sections.ts';
import { barGeom, chartReadout, summaryHtml, calendarHtml } from '../../lib/city-view.ts';
import {
  cityMastHtml, cityAnswerHtml, cityBodyHtml, cityVerdict, rulerTicksHtml, yearChart, allChartsHtml, chartSpec,
  whenWords, ratingWord,
} from '../../lib/place-city.ts';
import type { ChartVar, CityPageState } from '../../lib/place-city.ts';
import { verdictHtml, esc } from '../../lib/place-view.ts';
import { attachTouchScrub } from '../ui/touch-scrub.ts';
import type { ScrubElement } from '../ui/touch-scrub.ts';
import { openMyday, closeMyday, mydayOpen } from './myday-popover.ts';
import type { CitySeries } from '../../contracts/city.ts';

export interface CityHost {
  /** The element the page lives in; clicks are delegated from here. */
  scope: HTMLElement;
  /** Where each of the page's three parts is painted. */
  slots: { mast: HTMLElement; answer: HTMLElement; body: HTMLElement };
  series(): CitySeries | null;
  dates(): string[];
  /** Everything the renderers need, as the host currently has it. */
  pageState(): CityPageState;
  /** The reader picked a ten-day section, and perhaps a rating with it. */
  pick(i: number, act?: VarKey | null): void;
  setRating(act: VarKey): void;
  setRecipe(r: Recipe, opts?: { defer?: boolean }): void;
  /** Make "My kind of day" the reader's own: its row appears in the calendar. */
  unlockCustom(): void;
  openMap(): void;
  /** The briefing's play button. Absent on a guide, which has no ruler. */
  togglePlay?(): void;
  playing?(): boolean;
  /** The times of day on offer. Absent on a guide: it is midday throughout. */
  buckets?(): { key: string; label: string; hint: string; on: boolean }[];
  setBucket?(key: string): void;
  /** Whether the chart should draw at its narrow box. */
  narrow(): boolean;
}

let host: CityHost | null = null;
const wired = new WeakSet<HTMLElement>();

// The page's own furniture. Survives a re-render and a change of city, which is
// what a reader who chose "Rain" would expect.
const ui = {
  chartVar: 't' as ChartVar,
  chartMode: 'one' as 'one' | 'all',
  chartHover: null as number | null,
  calHover: null as { act: VarKey; i: number } | null,
  menu: null as null | 'rating' | 'bucket',
};

export const chartState = (): { chartVar: ChartVar } => ({ chartVar: ui.chartVar });

const $id = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T | null;

export function initCityPage(h: CityHost): void {
  host = h;
  wire(h.scope);
}

// Paint the whole page from the host's state. Not called on a pre-rendered
// page: there the markup is already in the document, and refreshCityPage fills
// in what a static file could not know.
export function paintCityPage(): void {
  if (!host) return;
  const s = host.series();
  if (!s) return;
  closeMyday();
  ui.menu = null;
  const st = { ...host.pageState(), chartVar: ui.chartVar };
  const dates = host.dates();
  host.slots.mast.innerHTML = cityMastHtml(s, st);
  host.slots.answer.innerHTML = cityAnswerHtml(s, dates, st);
  host.slots.body.innerHTML = cityBodyHtml(s, dates, st);
  refreshCityPage();
}

// The calendar's rows change when the rating list does (the custom row arriving,
// or its recipe being edited). Rebuilt in place; the guide's paragraphs are in
// the markup being replaced, so they are carried across.
export function rebuildCalendar(): void {
  if (!host) return;
  const s = host.series();
  const cal = $id('calendar');
  if (!s || !cal) return;
  const st = host.pageState();
  const prose: Record<string, string> = {};
  cal.querySelectorAll<HTMLElement>('.cal-row[data-act]').forEach((row) => {
    const p = row.querySelector('.cal-prose');
    if (p && row.dataset.act) prose[row.dataset.act] = p.innerHTML;
  });
  // The editor lives inside the row being replaced. Take it out first and put
  // it back after, so a slider mid-drag is the same element it was.
  const editor = cal.querySelector<HTMLElement>('.pl-myday');
  if (editor) editor.remove();
  cal.innerHTML = calendarHtml(s, host.dates(), st.acts, { curIdx: st.dekad, active: st.rating, prose, myday: true });
  if (editor) cal.querySelector('.cal-row-myday .pl-myday-anchor')?.append(editor);
  attachCalTouch(cal);
}

// What the verdict is about: the cell under the pointer if there is one, else
// what the page has selected.
function verdictTarget(st: CityPageState): { act: VarKey | null; i: number | null } {
  if (ui.calHover) return ui.calHover;
  return { act: st.rating, i: st.dekad };
}

function paintVerdict(): void {
  if (!host) return;
  const s = host.series();
  const el = $id('pl-verdict');
  if (!s || !el) return;
  const st = host.pageState();
  const t = verdictTarget(st);
  const html = verdictHtml(cityVerdict(s, host.dates(), t.act, t.i, st.recipe, st.acts));
  // A cell with no reading has no verdict. While the pointer is only passing
  // over it, the last one stays rather than the block emptying and the page
  // closing up under the pointer.
  if (!html && ui.calHover) return;
  el.innerHTML = html;
}

function paintChart(): void {
  if (!host) return;
  const s = host.series();
  const sec = $id('sec-year');
  if (!s || !sec) return;
  const dates = host.dates();
  const st = host.pageState();
  const idx = ui.chartHover != null ? ui.chartHover : st.dekad;
  sec.dataset.chartVar = ui.chartVar;
  sec.dataset.chartMode = ui.chartMode;
  sec.querySelectorAll<HTMLElement>('[data-for]').forEach((el) => { el.hidden = el.dataset.for !== ui.chartVar; });
  sec.querySelectorAll<HTMLElement>('[data-chart-var]').forEach((b) => setOn(b, b.dataset.chartVar === ui.chartVar && ui.chartMode === 'one'));
  sec.querySelectorAll<HTMLElement>('[data-chart-mode]').forEach((b) => setOn(b, b.dataset.chartMode === ui.chartMode));
  const all = $id('chart-all');
  if (all) {
    all.hidden = ui.chartMode !== 'all';
    if (ui.chartMode === 'all') all.innerHTML = allChartsHtml(s, dates, idx);
  }
  if (ui.chartMode === 'one') {
    const svg = sec.querySelector('#chart-main .chart-svg');
    if (svg) svg.innerHTML = yearChart(s, ui.chartVar, { curIdx: idx, wide: !host.narrow() });
    const readout = $id('chart-readout');
    if (readout) readout.textContent = idx != null ? chartReadout(s, chartSpec(ui.chartVar).id, idx, dates) : '';
  }
  const when = $id('sum-when');
  if (when) when.textContent = idx != null ? sectionLabel(dates[idx]) : 'The year';
  const sum = $id('summary');
  if (sum) sum.innerHTML = idx != null ? summaryHtml(s, idx, dates) : '';
}

function setOn(b: HTMLElement, on: boolean): void {
  b.classList.toggle('on', on);
  if (b.hasAttribute('aria-pressed')) b.setAttribute('aria-pressed', String(on));
}

// Everything that follows the selected section and rating. Cheap enough to run
// on every playback frame: strings into a handful of elements, classes on the
// rest.
export function refreshCityPage(): void {
  if (!host) return;
  const s = host.series();
  if (!s) return;
  const dates = host.dates();
  const st = host.pageState();
  const i = st.dekad;
  const act = st.rating;

  paintVerdict();

  const ticks = $id('pl-ticks');
  if (ticks) ticks.innerHTML = rulerTicksHtml(s, dates, act, i);
  const play = $id('pl-play');
  if (play && host.playing) play.textContent = host.playing() ? '❚❚' : '▶';

  const word = $id('pl-state-rating');
  if (word) word.textContent = ratingWord(act);
  const when = $id('pl-state-when');
  if (when) when.textContent = whenWords(dates, i);
  const bucket = $id<HTMLButtonElement>('pl-state-bucket');
  if (bucket) {
    bucket.textContent = st.bucket;
    bucket.disabled = !host.buckets;
  }

  const cal = $id('calendar');
  if (cal) {
    cal.querySelectorAll<HTMLElement>('.cal-row').forEach((row) => row.classList.toggle('active', !!act && row.dataset.act === act));
    cal.querySelectorAll<HTMLElement>('.cal-cell').forEach((c) => c.classList.toggle('cur', i != null && Number(c.dataset.i) === i));
    attachCalTouch(cal);
  }

  paintChart();
  attachChartTouch();

  const month = i == null ? -1 : Math.floor(i / 3);
  document.querySelectorAll<HTMLElement>('#month-table tbody tr').forEach((tr, m) => tr.classList.toggle('cur', m === month));
  host.scope.querySelectorAll<HTMLElement>('.pl-season').forEach((el) => {
    el.classList.toggle('cur', (el.dataset.months || '').split(',').map(Number).includes(month));
  });
  if (ui.menu) paintMenu();
}

// ── chart: hover reads a section, click goes to it ───────────────────────────
function eventToIdx(slot: HTMLElement, clientX: number): number {
  const r = slot.getBoundingClientRect();
  const n = host ? host.dates().length : 36;
  // barGeom's gaps are a fraction of its width, so any width gives the same
  // fractions; 1000 is just a round one to measure in.
  const vx = ((clientX - r.left) / (r.width || 1)) * 1000;
  const geom = barGeom(n, 1000);
  let best = 0, bd = Infinity;
  for (let k = 0; k < geom.length; k++) { const d = Math.abs(geom[k].cx - vx); if (d < bd) { bd = d; best = k; } }
  return best;
}

function setChartHover(idx: number | null): void {
  if (ui.chartHover === idx) return;
  ui.chartHover = idx;
  paintChart();
  document.querySelectorAll<HTMLElement>('#calendar .cal-cell').forEach((c) => c.classList.toggle('hov', idx != null && Number(c.dataset.i) === idx));
}

function attachChartTouch(): void {
  const slot = $id<ScrubElement>('chart-main');
  if (!slot || slot._scrubbed) return;
  slot._scrubbed = true;
  slot.addEventListener('mousemove', (e) => setChartHover(eventToIdx(slot, e.clientX)));
  slot.addEventListener('mouseleave', () => setChartHover(null));
  slot.addEventListener('click', (e) => {
    if (slot._squelchClick) { slot._squelchClick = false; return; }
    const idx = eventToIdx(slot, e.clientX);
    ui.chartHover = null;
    host?.pick(idx);
  });
  attachTouchScrub(slot, {
    drag: (x) => setChartHover(eventToIdx(slot, x)),
    end: (x) => { const idx = eventToIdx(slot, x); ui.chartHover = null; host?.pick(idx); },
    cancel: () => setChartHover(null),
  });
}

// ── calendar: hover states a cell in the verdict, click picks it ─────────────
function cellInfo(cell: HTMLElement): { act: VarKey; i: number } | null {
  const row = cell.closest<HTMLElement>('.cal-row');
  const act = row?.dataset.act as VarKey | undefined;
  const i = Number(cell.dataset.i);
  return act && VAR[act] && Number.isInteger(i) ? { act, i } : null;
}

function setCalHover(next: { act: VarKey; i: number } | null): void {
  const cur = ui.calHover;
  if (cur === next || (cur && next && cur.act === next.act && cur.i === next.i)) return;
  ui.calHover = next;
  paintVerdict();
}

// Touch: dragging across a rating's strip previews each cell; releasing picks
// the one under the finger. Per strip, so a vertical scroll that starts on a
// name is never captured — and re-attached after a rebuild, which replaces the
// strips with fresh nodes.
function attachCalTouch(cal: HTMLElement): void {
  const cellAt = (x: number, y: number) => {
    const el = document.elementFromPoint(x, y);
    return el && el.closest ? el.closest<HTMLElement>('.cal-cell') : null;
  };
  cal.querySelectorAll<ScrubElement>('.cal-cells').forEach((strip) => {
    if (strip._scrubbed || strip.tagName === 'BUTTON') return;
    strip._scrubbed = true;
    attachTouchScrub(strip, {
      drag: (x, y) => { const c = cellAt(x, y); if (c) setCalHover(cellInfo(c)); },
      end: (x, y) => {
        const c = cellAt(x, y);
        const info = c ? cellInfo(c) : null;
        ui.calHover = null;
        if (info) host?.pick(info.i, info.act);
        else paintVerdict();
      },
      cancel: () => setCalHover(null),
    });
  });
}

// ── the bar's menus ──────────────────────────────────────────────────────────
function paintMenu(): void {
  const menu = $id('pl-menu');
  if (!menu || !host) return;
  const s = host.series();
  menu.hidden = !ui.menu;
  $id('pl-state-rating')?.setAttribute('aria-expanded', String(ui.menu === 'rating'));
  if (!ui.menu || !s) return;
  const st = host.pageState();
  if (ui.menu === 'rating') {
    const rows = st.acts.map((a) => {
      const v = st.dekad != null ? (s.series[a] || [])[st.dekad] : null;
      return `<button type="button" class="pl-menu-row${a === st.rating ? ' on' : ''}" role="menuitem" data-menu-rating="${a}"><span>${esc(VAR[a].label)}</span><span>${v != null ? Math.round(v) : '—'}</span></button>`;
    }).join('');
    const mine = st.acts.some((a) => VAR[a].custom) ? '' : `<button type="button" class="pl-menu-row" role="menuitem" data-myday-open><span>My kind of day</span><span>›</span></button>`;
    menu.innerHTML = rows + mine;
  } else {
    menu.innerHTML = (host.buckets ? host.buckets() : [])
      .map((b) => `<button type="button" class="pl-menu-row${b.on ? ' on' : ''}" role="menuitem" data-menu-bucket="${esc(b.key)}"><span>${esc(b.label)}</span><span>${esc(b.hint)}</span></button>`)
      .join('');
  }
}

function setMenu(m: typeof ui.menu): void {
  ui.menu = m;
  paintMenu();
}

// ── "My kind of day" ─────────────────────────────────────────────────────────
function openEditor(): void {
  if (!host) return;
  const h = host;
  setMenu(null);
  // Opening it IS choosing the rating: the editor is a view onto a recipe the
  // page is showing, and editing one it is not would be two things at once.
  h.unlockCustom();
  h.setRating('custom');
  const anchor = document.querySelector<HTMLElement>('#calendar .cal-row-myday .pl-myday-anchor');
  if (!anchor) return;
  openMyday(anchor, { recipe: () => h.pageState().recipe, setRecipe: (r, o) => h.setRecipe(r, o) }, () => {});
  anchor.closest('.cal-row')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

// ── wiring: one set of delegated handlers per scope ──────────────────────────
function wire(scope: HTMLElement): void {
  if (wired.has(scope)) return;
  wired.add(scope);

  scope.addEventListener('click', (e) => {
    if (!host) return;
    const t = e.target as HTMLElement;
    if (!t || !t.closest) return;

    if (t.closest('[data-myday-open]')) { e.preventDefault(); e.stopPropagation(); openEditor(); return; }

    const cell = t.closest<HTMLElement>('#calendar .cal-cell');
    if (cell) {
      // After a drag, swallow the browser's trailing synthetic click.
      const strip = cell.closest<ScrubElement>('.cal-cells');
      if (strip && strip._squelchClick) { strip._squelchClick = false; return; }
      const info = cellInfo(cell);
      if (info) host.pick(info.i, info.act);
      return;
    }
    const label = t.closest<HTMLElement>('#calendar [data-pick-a]');
    if (label) {
      const act = label.closest<HTMLElement>('.cal-row')?.dataset.act as VarKey | undefined;
      if (act && VAR[act]) host.setRating(act);
      return;
    }

    const menuRating = t.closest<HTMLElement>('[data-menu-rating]');
    if (menuRating) { setMenu(null); host.setRating(menuRating.dataset.menuRating as VarKey); return; }
    const menuBucket = t.closest<HTMLElement>('[data-menu-bucket]');
    if (menuBucket) { setMenu(null); host.setBucket?.(menuBucket.dataset.menuBucket!); return; }
    if (t.closest('[data-pl-rating-menu]')) { e.stopPropagation(); setMenu(ui.menu === 'rating' ? null : 'rating'); return; }
    if (t.closest('[data-pl-bucket-menu]')) {
      e.stopPropagation();
      if (host.buckets) setMenu(ui.menu === 'bucket' ? null : 'bucket');
      return;
    }
    if (t.closest('[data-pl-when]')) { scrollToId(host.playing ? 'pl-ticks' : 'sec-when'); return; }

    const chartVar = t.closest<HTMLElement>('[data-chart-var]');
    if (chartVar && chartVar.tagName === 'BUTTON') { ui.chartVar = chartVar.dataset.chartVar as ChartVar; ui.chartMode = 'one'; paintChart(); return; }
    const chartMode = t.closest<HTMLElement>('[data-chart-mode]');
    if (chartMode && chartMode.tagName === 'BUTTON') { ui.chartMode = chartMode.dataset.chartMode === 'all' ? 'all' : 'one'; paintChart(); return; }
    const chartOpen = t.closest<HTMLElement>('[data-chart-open]');
    if (chartOpen) { ui.chartVar = chartOpen.dataset.chartOpen as ChartVar; ui.chartMode = 'one'; paintChart(); return; }

    if (t.closest('[data-pl-play]')) { host.togglePlay?.(); return; }
    if (t.closest('[data-pl-map]')) { host.openMap(); return; }

    // Every date printed on the page goes to that date: a record, a peak, a
    // ruler tick, a month's row, a season's card.
    const go = t.closest<HTMLElement>('[data-goto-i]');
    if (go && !t.closest('a')) {
      const i = Number(go.dataset.gotoI);
      if (!Number.isInteger(i)) return;
      const a = go.dataset.gotoA == null ? null : Number(go.dataset.gotoA);
      const act = a != null ? (go.closest<HTMLElement>('.cal-row')?.dataset.act as VarKey | undefined) : undefined;
      host.pick(i, act && VAR[act] ? act : null);
      return;
    }

    // Section links scroll the page's own scroller, and stay out of the address.
    const link = t.closest<HTMLAnchorElement>('a.pl-bar-link');
    if (link) {
      const id = (link.getAttribute('href') || '').slice(1);
      if (id) { e.preventDefault(); scrollToId(id); }
    }
  });

  // mouseover/mouseout rather than enter/leave: only those bubble to here.
  scope.addEventListener('mouseover', (e) => {
    const cell = (e.target as HTMLElement).closest?.<HTMLElement>('#calendar .cal-cell');
    if (cell) setCalHover(cellInfo(cell));
  });
  scope.addEventListener('mouseout', (e) => {
    const from = (e.target as HTMLElement).closest?.('#calendar .cal-cell');
    const to = ((e as MouseEvent).relatedTarget as HTMLElement | null)?.closest?.('#calendar .cal-cell');
    if (from && !to) setCalHover(null);
  });

  // A click anywhere else closes what is open.
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (ui.menu && !(t.closest && t.closest('#pl-menu'))) setMenu(null);
    if (mydayOpen() && !(t.closest && t.closest('.pl-myday, [data-myday-open]'))) closeMyday();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (mydayOpen()) { closeMyday(); e.stopPropagation(); } else if (ui.menu) { setMenu(null); e.stopPropagation(); }
  }, true);
}

function scrollToId(id: string): void {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export { closeMyday };
